import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { turnOffWebPushBeforeSignOut } from '../lib/webPush';
import { Box, CircularProgress, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!user) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for detached users (removed from company) — skip for platform admins or transient fallback users
  const isDetached = !user.is_fallback && !user.is_platform_admin && !user.trade_company_id && !user.customer_id;
  if (isDetached) {
    // Switch this browser's push off while the session still exists.
    void turnOffWebPushBeforeSignOut().finally(() => supabase.auth.signOut());
    return <Navigate to="/login?error=account_detached" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
